import * as React from 'react';
import { CHART_COLORS, CHART_INK, formatCompact } from './chart-utils';

export type HorizontalBarItem = {
  key: string;
  label: string;
  value: number;
  secondary?: string;
};

export type HorizontalBarListProps = {
  items: HorizontalBarItem[];
  valueFormat?: (n: number) => string;
  max?: number;
  color?: string;
  emptyLabel?: string;
  className?: string;
};

/** One row per category: label, single-hue bar, value at the tip. */
export function HorizontalBarList({
  items,
  valueFormat = formatCompact,
  max,
  color = CHART_COLORS[0],
  emptyLabel = 'No data',
  className,
}: HorizontalBarListProps) {
  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-sm" style={{ color: CHART_INK.muted }}>
        {emptyLabel}
      </div>
    );
  }
  const limit = max ?? Math.max(...items.map((i) => i.value), 0);
  return (
    <ul className={['space-y-2.5', className].filter(Boolean).join(' ')} data-testid="horizontal-bar-list">
      {items.map((item) => {
        const pct = limit > 0 ? Math.max(0, Math.min(100, (item.value / limit) * 100)) : 0;
        return (
          <li
            key={item.key}
            data-testid="horizontal-bar-item"
            className="grid items-center gap-3"
            style={{ gridTemplateColumns: 'minmax(0, 11rem) minmax(0, 1fr) auto', fontSize: 13 }}
            title={`${item.label}: ${valueFormat(item.value)}`}
          >
            <span className="truncate" style={{ color: CHART_INK.primary }}>
              {item.label}
            </span>
            <span className="block h-2.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: CHART_INK.track }}>
              <span
                className="block h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </span>
            <span className="flex items-baseline gap-2 whitespace-nowrap">
              <span className="font-medium tabular-nums" style={{ color: CHART_INK.primary }}>
                {valueFormat(item.value)}
              </span>
              {item.secondary ? (
                <span style={{ color: CHART_INK.muted, fontSize: 12 }}>{item.secondary}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
